import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, MessageSquare, Plus, Search, Shield, Menu } from "lucide-react";
import { DesktopNavMenu } from "@/components/DesktopNavMenu";
import { useAuth } from "@/context/AuthContext";
import { api, cachedGet } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ROLE_LABELS } from "@/lib/constants";
import { BRAND, CiftoraLogo } from "@/lib/brand";
import { CookieConsent } from "@/components/CookieConsent";
import { ProfileGate } from "@/components/ProfileGate";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PushPrompt } from "@/components/PushPrompt";
import { MobileTabBar } from "@/components/mobile/MobileTabBar";
import { MobileMenuSheet } from "@/components/mobile/MobileMenuSheet";
import { PullToRefresh } from "@/components/mobile/PullToRefresh";
import { OfflineBanner } from "@/components/mobile/OfflineBanner";
import { captureUtm, trackEvent, trackNav, trackPageView } from "@/lib/analytics";
import { initMetaPixel } from "@/lib/metaPixel";

const STAFF = ["Moderator", "SupportAgent", "Admin", "SuperAdmin"];
export default function SiteLayout() {
  const { user, logout } = useAuth();
  const isVet = !!user && (user.is_veterinarian || user.role === "Veterinarian");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    captureUtm();
    initMetaPixel();
    const onConsent = () => initMetaPixel();
    window.addEventListener("ciftora-consent-changed", onConsent);
    return () => window.removeEventListener("ciftora-consent-changed", onConsent);
  }, []);

  useEffect(() => {
    trackPageView(pathname, document.title);
  }, [pathname]);

  useEffect(() => {
    const onClick = (event) => {
      const link = event.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      if (href.includes("wa.me") || href.includes("whatsapp")) {
        trackEvent("WHATSAPP_CLICK", { from: window.location.pathname });
        return;
      }
      const map = [["/ilanlar", "MARKET"], ["/ciftligim", "CIFTLIGIM"],
        ["/veteriner", "VETERINER"], ["/nakliy", "NAKLIYE"], ["/fuar", "FUAR"],
        ["/kurban", "KURBAN"], ["/kasap", "KASAP"], ["rasyon", "RASYON"],
        ["/forum", "FORUM"], ["/ai", "AI"], ["/fiyat", "PRICE_CENTER"]];
      const hit = map.find(([prefix]) => href.startsWith(prefix) || href.includes(prefix));
      if (hit) trackNav(hit[1], { href: href.slice(0, 80) });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    cachedGet("/catalog/categories").then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || STAFF.includes(user.role)) {
      setUnread(0);
      return;
    }
    api
      .get("/conversations")
      .then(({ data }) => setUnread(data.reduce((s, c) => s + (c.unread || 0), 0)))
      .catch(() => {});
  }, [user, pathname]);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/ilanlar?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 lg:px-8">
          <Link to="/" data-testid="logo-link" className="flex items-center">
            <CiftoraLogo variant="header" className="hidden h-11 sm:block" testid="header-logo" />
            <CiftoraLogo variant="symbol" className="h-10 sm:hidden" testid="header-logo-symbol" />
          </Link>

          <form onSubmit={submit} className="hidden flex-1 md:block">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3">
              <Search className="h-5 w-5 text-muted-foreground" />
              <input
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Hayvan, ekipman, yem veya hizmet ara"
                className="h-12 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
              />
            </div>
          </form>

          <nav className="ml-auto hidden items-center gap-1 md:flex">
            {!user && (
              <NavLink
                to="/ilanlar"
                data-testid="nav-listings"
                className="rounded-lg px-3 py-2 text-sm font-semibold text-primary hover:bg-muted"
              >
                İlanlar
              </NavLink>
            )}
            {user ? (
              <>
                <DesktopNavMenu user={user} isVet={isVet} />
                {STAFF.includes(user.role) && (
                  <NavLink
                    to="/yonetim"
                    data-testid="nav-admin"
                    className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-secondary hover:bg-muted"
                  >
                    <Shield className="h-4 w-4" /> Yönetim
                  </NavLink>
                )}
                <Button asChild data-testid="nav-create-listing" className="h-11 rounded-full px-5">
                  <Link to="/ilan-ver">
                    <Plus className="mr-1 h-4 w-4" /> İlan Ver
                  </Link>
                </Button>
                <button
                  data-testid="nav-logout"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                  aria-label="Çıkış"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/giris"
                  data-testid="nav-login"
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-primary hover:bg-muted"
                >
                  Giriş Yap
                </Link>
                <Button asChild data-testid="nav-register" className="h-11 rounded-full px-5">
                  <Link to="/kayit">Ücretsiz Üye Ol</Link>
                </Button>
              </>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-1 md:hidden">
            <Link
              to={user ? "/panel/mesajlar" : "/giris"}
              data-testid="mobile-messages-btn"
              aria-label="Mesajlar"
              className="app-tap relative flex h-11 w-11 items-center justify-center rounded-full text-primary"
            >
              <MessageSquare className="h-6 w-6" />
              {unread > 0 && (
                <span
                  data-testid="mobile-unread-badge"
                  className="absolute right-1 top-1 min-w-[18px] rounded-full bg-destructive px-1 text-[10px] font-bold leading-[18px] text-destructive-foreground"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
            <button
              className="app-tap flex h-11 w-11 items-center justify-center rounded-full text-primary"
              data-testid="mobile-menu-btn"
              onClick={() => setOpen(true)}
              aria-label="Menü"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-4 pb-3 md:hidden">
          <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4">
            <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
            <input
              data-testid="mobile-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Hayvan, ekipman, yem ara"
              className="h-11 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
            />
          </div>
        </form>

        <MobileMenuSheet
          open={open}
          onClose={() => setOpen(false)}
          cats={cats}
          user={user}
          isVet={isVet}
          onLogout={async () => {
            await logout();
            setOpen(false);
            navigate("/");
          }}
        />

        <div className="no-scrollbar flex gap-2 overflow-x-auto px-4 pb-3 md:hidden">
          {cats.slice(0, 12).map((c) => (
            <Link
              key={c.id}
              to={`/ilanlar?category_id=${c.id}`}
              data-testid={`mobile-cat-chip-${c.id}`}
              className="app-tap whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-primary"
            >
              {c.name}
            </Link>
          ))}
        </div>

        <div className="hidden border-t border-border bg-card md:block">
          <div className="no-scrollbar mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 lg:px-8">
            {cats.map((c) => (
              <Link
                key={c.id}
                to={`/ilanlar?category_id=${c.id}`}
                data-testid={`cat-nav-${c.id}`}
                className="whitespace-nowrap px-3 py-2.5 text-sm font-medium text-primary transition-colors duration-150 hover:text-accent-foreground hover:underline"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </header>

      <PullToRefresh>
        <main className="flex-1" key={pathname} data-testid="site-main">
          <div className="route-in">
            <ProfileGate>
              <Outlet />
            </ProfileGate>
          </div>
        </main>
      </PullToRefresh>
      <CookieConsent />
      <InstallPrompt />
      <PushPrompt />

      <footer className="border-t border-border bg-primary text-primary-foreground">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
          <div>
            <CiftoraLogo variant="white" className="h-14" testid="footer-logo" />
            <p className="mt-3 text-sm opacity-80">{BRAND.slogan}</p>
            <p className="mt-4 text-sm opacity-70">{BRAND.productMessage}</p>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider opacity-70">Market</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ilanlar" className="opacity-85 hover:underline">Tüm İlanlar</Link></li>
              <li><Link to="/ilanlar?listing_type=Aranıyor" className="opacity-85 hover:underline">Aranıyor İlanları</Link></li>
              <li><Link to="/ilan-ver" className="opacity-85 hover:underline">Ücretsiz İlan Ver</Link></li>
              <li><Link to="/mezat" className="opacity-85 hover:underline">Ciftora Mezat</Link></li>
              <li><Link to="/magazalar" className="opacity-85 hover:underline">Kurumsal Mağazalar</Link></li>
              <li><Link to="/nakliye" className="opacity-85 hover:underline">Nakliye Ağı</Link></li>
              <li><Link to="/veteriner-bul" className="opacity-85 hover:underline">Veteriner Hekim Bul</Link></li>
              <li><Link to="/kasap-bul" className="opacity-85 hover:underline">Kasap & Kesimhane Bul</Link></li>
              <li><Link to="/degerleme" className="opacity-85 hover:underline">Değerleme</Link></li>
              <li><Link to="/kooperatif" className="opacity-85 hover:underline">Kooperatif & Birlik</Link></li>
              <li><Link to="/planlar" className="opacity-85 hover:underline">Planlar</Link></li>            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider opacity-70">Kurumsal</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/sayfa/hakkimizda" className="opacity-85 hover:underline">Hakkımızda</Link></li>
              <li><Link to="/sayfa/nasil-calisir" className="opacity-85 hover:underline">Nasıl Çalışır?</Link></li>
              <li><Link to="/sayfa/guvenli-ticaret-ilkeleri" className="opacity-85 hover:underline">Güvenli Ticaret İlkeleri</Link></li>
              <li><Link to="/sayfa/ilan-kurallari" className="opacity-85 hover:underline">İlan Kuralları</Link></li>
              <li><Link to="/sayfa/kullanim-kosullari" className="opacity-85 hover:underline">Kullanım Koşulları</Link></li>
              <li><Link to="/sayfa/gizlilik-politikasi" className="opacity-85 hover:underline">Gizlilik Politikası</Link></li>
              <li><Link to="/sayfa/kvkk-aydinlatma-metni" className="opacity-85 hover:underline">KVKK Aydınlatma Metni</Link></li>
              <li><Link to="/sayfa/cerez-politikasi" className="opacity-85 hover:underline">Çerez Politikası</Link></li>
              <li><Link to="/sayfa/iletisim" className="opacity-85 hover:underline">İletişim</Link></li>
            </ul>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider opacity-70">Çiftliğim</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ciftligim" className="opacity-85 hover:underline">Çiftliklerim</Link></li>
            </ul>
            <p className="mt-3 text-sm opacity-85">
              Sürü takibi, süt ve besi performansı, sağlık, yem stoku, finans ve raporlama.
            </p>
          </div>
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-wider opacity-70">Akıllı Çiftlik</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/ciftora-ai" className="opacity-85 hover:underline">Ciftora AI</Link></li>
              <li><Link to="/kopek-mama-hesapla" data-testid="footer-pet-calc-link" className="opacity-85 hover:underline">Pet Mama Hesapla</Link></li>
              <li><Link to="/kilavuz" data-testid="footer-guide-link" className="opacity-85 hover:underline">Kullanım Kılavuzu</Link></li>
              <li><Link to="/bilgi-merkezi" className="opacity-85 hover:underline">Sağlık Bilgi Merkezi</Link></li>
              <li><Link to="/bildirimler" className="opacity-85 hover:underline">Bildirim tercihleri</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-5 text-center text-xs opacity-70">
          © {new Date().getFullYear()} Ciftora · ciftora.com · Ciftora doğrulamaları resmî kurum
          doğrulaması değildir.
        </div>
      </footer>

      <MobileTabBar />
      <OfflineBanner />
    </div>
  );
}

export const RoleLabel = ({ role }) => <span>{ROLE_LABELS[role] || role}</span>;
export const MessagesIcon = MessageSquare;
